import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, Rocket, Palette, Phone, FileText, Eye, ExternalLink, CheckCircle2, Clock,
  Video, CalendarDays, Plus, Trash2, LayoutTemplate, Download, Server,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { formatApiErrorDetail } from "@/lib/api";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1516117172878-fd2c41f4a759?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzbW9reSUyMG1vdW50YWlucyUyMHN1bnJpc2UlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgzOTA0NjQ2fDA&ixlib=rb-4.1.0&q=85";

const empty = {
  church_name: "", tagline: "", about: "", service_times: "",
  address: "", phone: "", email: "", theme_color: "#D97706", hero_image: "",
  template: "modern", site_type: "single", sermons: [], events: [], status: "draft",
};

const TEMPLATES = [
  { id: "modern", label: "Modern", desc: "Clean, bold, image-forward" },
  { id: "traditional", label: "Traditional", desc: "Classic, serif, timeless" },
  { id: "youth", label: "Youth", desc: "Vibrant, energetic, playful" },
];

/**
 * Reusable church website editor used by both the client portal and the admin console.
 * Props:
 *  - getSite:     async () => ({ content, hosting_mode, church_id })
 *  - saveSite:    async (content) => any
 *  - publishSite: async () => any
 *  - canEdit:     boolean (false => read-only)
 *  - exportPrefix:string|null  API prefix for self-managed downloads (e.g. "/site"); null hides them
 */
export default function SiteEditor({ getSite, saveSite, publishSite, canEdit = true, exportPrefix = null }) {
  const [content, setContent] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hostingMode, setHostingMode] = useState("hosted");
  const [churchId, setChurchId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSite();
      setContent({ ...empty, ...(data.content || {}) });
      setHostingMode(data.hosting_mode || "hosted");
      setChurchId(data.church_id || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load the site");
    } finally {
      setLoading(false);
    }
  }, [getSite]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setContent((c) => ({ ...c, [k]: e.target.value }));
  const addItem = (key, blank) => setContent((c) => ({ ...c, [key]: [...(c[key] || []), blank] }));
  const removeItem = (key, idx) => setContent((c) => ({ ...c, [key]: c[key].filter((_, i) => i !== idx) }));
  const updateItem = (key, idx, field) => (e) =>
    setContent((c) => ({ ...c, [key]: c[key].map((it, i) => (i === idx ? { ...it, [field]: e.target.value } : it)) }));

  const save = async () => {
    setSaving(true);
    try {
      await saveSite(content);
      toast.success("Changes saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      await saveSite(content);
      await publishSite();
      setContent((c) => ({ ...c, status: "published" }));
      toast.success("Website published!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed");
    } finally {
      setSaving(false);
    }
  };

  const fname = (ext) => `${(content.church_name || "church-site").toLowerCase().replace(/\s+/g, "-")}.${ext}`;
  const blobDl = (res, ext, type) => {
    const url = window.URL.createObjectURL(new Blob([res.data], { type }));
    const a = document.createElement("a");
    a.href = url; a.download = fname(ext);
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };
  const downloadZip = async () => {
    try { blobDl(await api.get(`${exportPrefix}/export/zip`, { responseType: "blob" }), "zip", "application/zip"); toast.success("Site package downloaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed"); }
  };
  const downloadHtml = async () => {
    try { blobDl(await api.get(`${exportPrefix}/export`, { responseType: "blob" }), "html", "text/html"); toast.success("Website downloaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed"); }
  };
  const copyLink = async () => {
    if (!churchId) return;
    const link = `${window.location.origin}/view/${churchId}`;
    try { await navigator.clipboard.writeText(link); toast.success("Public site link copied"); }
    catch { toast.message("Public site link", { description: link }); }
  };

  if (loading) {
    return <div className="py-16 grid place-items-center"><div className="h-9 w-9 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" /></div>;
  }

  const tplStyle = {
    modern: { heading: "font-display font-bold", hero: "items-start text-left", overlay: "bg-slate-900/55", btn: "rounded-full", cta: "Join Us Sunday" },
    traditional: { heading: "font-display font-semibold italic", hero: "items-center text-center", overlay: "bg-slate-900/65", btn: "rounded-none border border-white/70", cta: "You Are Welcome Here" },
    youth: { heading: "font-body font-extrabold uppercase tracking-tight", hero: "items-center text-center", overlay: "bg-slate-900/45", btn: "rounded-full uppercase tracking-wide", cta: "Come Hang With Us" },
  }[content.template || "modern"];

  return (
    <div data-testid="site-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 text-sm">
          {content.status === "published" ? (
            <span className="inline-flex items-center gap-1.5 text-green-700" data-testid="editor-status"><CheckCircle2 className="h-4 w-4" /> Published</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-amber-700" data-testid="editor-status"><Clock className="h-4 w-4" /> Draft — not yet live</span>
          )}
          <span className="text-slate-300">·</span>
          <span className="text-slate-500">{hostingMode === "self_managed" ? "Self-managed" : "Hosted by us"}</span>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={saving} variant="outline" data-testid="save-btn" className="rounded-md"><Save className="h-4 w-4 mr-2" /> Save</Button>
            <Button onClick={publish} disabled={saving} data-testid="publish-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white"><Rocket className="h-4 w-4 mr-2" /> Publish</Button>
          </div>
        )}
      </div>

      {exportPrefix && hostingMode === "self_managed" && (
        <div className="mb-6" data-testid="self-managed-banner">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Server className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-blue-900">Self-managed hosting</div>
                <div className="text-sm text-blue-800">Edit anytime here, then download the latest files to publish on your own hosting.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadZip} data-testid="download-zip-btn" className="rounded-md bg-blue-600 hover:bg-blue-700 text-white"><Download className="h-4 w-4 mr-2" /> Download ZIP</Button>
              <Button onClick={downloadHtml} variant="outline" data-testid="download-site-btn" className="rounded-md border-blue-200 text-blue-700 hover:bg-blue-100"><Download className="h-4 w-4 mr-2" /> HTML only</Button>
              <Button onClick={copyLink} variant="outline" data-testid="copy-public-link-btn" className="rounded-md border-blue-200 text-blue-700 hover:bg-blue-100"><ExternalLink className="h-4 w-4 mr-2" /> Copy link</Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Editor */}
        <div>
          <Tabs defaultValue="content" className="w-full">
            <TabsList className="grid grid-cols-5 w-full" data-testid="editor-tabs">
              <TabsTrigger value="content" data-testid="tab-content"><FileText className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Content</span></TabsTrigger>
              <TabsTrigger value="sermons" data-testid="tab-sermons"><Video className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Sermons</span></TabsTrigger>
              <TabsTrigger value="events" data-testid="tab-events"><CalendarDays className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Events</span></TabsTrigger>
              <TabsTrigger value="contact" data-testid="tab-contact"><Phone className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Contact</span></TabsTrigger>
              <TabsTrigger value="appearance" data-testid="tab-appearance"><Palette className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Style</span></TabsTrigger>
            </TabsList>

            <fieldset disabled={!canEdit} className="min-w-0 border-0 p-0 m-0 disabled:opacity-80">
              <TabsContent value="content">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader><CardTitle className="font-display">Church Details</CardTitle><CardDescription>The heart of your website.</CardDescription></CardHeader>
                  <CardContent className="space-y-5">
                    <div><Label>Church / Ministry Name</Label><Input className="mt-1.5" value={content.church_name} onChange={set("church_name")} data-testid="input-church-name" placeholder="Grace Community Church" /></div>
                    <div><Label>Tagline</Label><Input className="mt-1.5" value={content.tagline} onChange={set("tagline")} data-testid="input-tagline" placeholder="A place to belong, believe, and become" /></div>
                    <div><Label>About Us</Label><Textarea className="mt-1.5" rows={5} value={content.about} onChange={set("about")} data-testid="input-about" placeholder="Share your mission and story..." /></div>
                    <div><Label>Service Times</Label><Input className="mt-1.5" value={content.service_times} onChange={set("service_times")} data-testid="input-service-times" placeholder="Sundays 9:00 AM & 11:00 AM" /></div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="sermons">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader><CardTitle className="font-display">Sermons</CardTitle><CardDescription>Share recent messages with your congregation.</CardDescription></CardHeader>
                  <CardContent className="space-y-4">
                    {content.sermons.length === 0 && <p className="text-sm text-slate-400" data-testid="sermons-empty">No sermons yet. Add your first message below.</p>}
                    {content.sermons.map((s, i) => (
                      <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`sermon-item-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Sermon {i + 1}</span>
                          <button onClick={() => removeItem("sermons", i)} data-testid={`sermon-remove-${i}`} className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 className="h-4 w-4" /></button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <Input placeholder="Title" value={s.title} onChange={updateItem("sermons", i, "title")} data-testid={`sermon-title-${i}`} />
                          <Input placeholder="Speaker" value={s.speaker} onChange={updateItem("sermons", i, "speaker")} data-testid={`sermon-speaker-${i}`} />
                          <Input placeholder="Date (e.g. Jun 8, 2025)" value={s.date} onChange={updateItem("sermons", i, "date")} data-testid={`sermon-date-${i}`} />
                          <Input placeholder="Video/Audio link" value={s.link} onChange={updateItem("sermons", i, "link")} data-testid={`sermon-link-${i}`} />
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" className="rounded-md" data-testid="add-sermon-btn" onClick={() => addItem("sermons", { title: "", speaker: "", date: "", link: "" })}><Plus className="h-4 w-4 mr-2" /> Add Sermon</Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="events">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader><CardTitle className="font-display">Upcoming Events</CardTitle><CardDescription>Keep your community in the loop.</CardDescription></CardHeader>
                  <CardContent className="space-y-4">
                    {content.events.length === 0 && <p className="text-sm text-slate-400" data-testid="events-empty">No events yet. Add your first event below.</p>}
                    {content.events.map((ev, i) => (
                      <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`event-item-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Event {i + 1}</span>
                          <button onClick={() => removeItem("events", i)} data-testid={`event-remove-${i}`} className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 className="h-4 w-4" /></button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <Input placeholder="Event title" value={ev.title} onChange={updateItem("events", i, "title")} data-testid={`event-title-${i}`} />
                          <Input placeholder="Date & time" value={ev.date} onChange={updateItem("events", i, "date")} data-testid={`event-date-${i}`} />
                          <Input placeholder="Location" value={ev.location} onChange={updateItem("events", i, "location")} data-testid={`event-location-${i}`} />
                          <Input placeholder="Short description" value={ev.description} onChange={updateItem("events", i, "description")} data-testid={`event-description-${i}`} />
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" className="rounded-md" data-testid="add-event-btn" onClick={() => addItem("events", { title: "", date: "", location: "", description: "" })}><Plus className="h-4 w-4 mr-2" /> Add Event</Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="contact">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader><CardTitle className="font-display">Contact Information</CardTitle><CardDescription>Help your congregation reach you.</CardDescription></CardHeader>
                  <CardContent className="space-y-5">
                    <div><Label>Address</Label><Input className="mt-1.5" value={content.address} onChange={set("address")} data-testid="input-address" placeholder="123 Faith Ave, Nashville, TN" /></div>
                    <div><Label>Phone</Label><Input className="mt-1.5" value={content.phone} onChange={set("phone")} data-testid="input-phone" placeholder="(615) 555-0100" /></div>
                    <div><Label>Email</Label><Input className="mt-1.5" value={content.email} onChange={set("email")} data-testid="input-email" placeholder="hello@church.org" /></div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="appearance">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader><CardTitle className="font-display">Appearance</CardTitle><CardDescription>Make it yours.</CardDescription></CardHeader>
                  <CardContent className="space-y-5">
                    <div>
                      <Label>Template</Label>
                      <div className="mt-2 grid grid-cols-3 gap-3" data-testid="template-picker">
                        {TEMPLATES.map((t) => (
                          <button key={t.id} type="button" data-testid={`template-${t.id}`} onClick={() => setContent((c) => ({ ...c, template: t.id }))}
                            className={`rounded-lg border p-3 text-left transition-colors duration-200 ${content.template === t.id ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-slate-200 hover:border-slate-300"}`}>
                            <LayoutTemplate className={`h-4 w-4 ${content.template === t.id ? "text-amber-600" : "text-slate-400"}`} />
                            <div className="mt-2 text-sm font-semibold text-slate-800">{t.label}</div>
                            <div className="text-xs text-slate-500">{t.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Accent Color</Label>
                      <div className="mt-1.5 flex items-center gap-3">
                        <input type="color" value={content.theme_color} onChange={set("theme_color")} data-testid="input-theme-color" className="h-10 w-16 rounded border border-slate-200 cursor-pointer" />
                        <span className="text-sm text-slate-500">{content.theme_color}</span>
                      </div>
                    </div>
                    <div><Label>Hero Image URL</Label><Input className="mt-1.5" value={content.hero_image} onChange={set("hero_image")} data-testid="input-hero-image" placeholder="https://... (leave blank for default)" /></div>
                  </CardContent>
                </Card>
              </TabsContent>
            </fieldset>
          </Tabs>
        </div>

        {/* Live Preview */}
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-600 mb-3">
            <Eye className="h-4 w-4" /> Live Preview
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400"><ExternalLink className="h-3 w-3" /> This is how visitors see the site</span>
          </div>
          <div data-testid="site-preview" className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-white">
            <div className="relative h-56">
              <img src={content.hero_image || HERO_FALLBACK} alt="Hero" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }} />
              <div className={`absolute inset-0 ${tplStyle.overlay}`} />
              <div className={`absolute inset-0 flex flex-col justify-center px-6 text-white ${tplStyle.hero}`}>
                <h2 className={`${tplStyle.heading} text-3xl drop-shadow`}>{content.church_name || "Your Church Name"}</h2>
                <p className="mt-2 text-slate-100 text-sm max-w-md">{content.tagline || "Your inspiring tagline goes here"}</p>
                <span className={`mt-4 px-5 py-2 text-sm font-semibold text-white ${tplStyle.btn}`} style={{ backgroundColor: content.theme_color }}>{tplStyle.cta}</span>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <h3 className="font-display text-lg font-bold" style={{ color: content.theme_color }}>About Us</h3>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{content.about || "Tell your community who you are and what you believe. This preview updates as you type."}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="rounded-lg bg-slate-50 p-3"><div className="font-semibold text-slate-800">Service Times</div><div className="text-slate-600">{content.service_times || "Sundays at 10 AM"}</div></div>
                <div className="rounded-lg bg-slate-50 p-3"><div className="font-semibold text-slate-800">Visit Us</div><div className="text-slate-600">{content.address || "Your address"}</div></div>
              </div>
              {content.sermons?.length > 0 && (
                <div data-testid="preview-sermons">
                  <h3 className="font-display text-lg font-bold" style={{ color: content.theme_color }}>Recent Sermons</h3>
                  <div className="mt-2 space-y-2">
                    {content.sermons.map((s, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                        <Video className="h-4 w-4 shrink-0" style={{ color: content.theme_color }} />
                        <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{s.title || "Untitled sermon"}</div><div className="text-xs text-slate-500">{[s.speaker, s.date].filter(Boolean).join(" · ")}</div></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {content.events?.length > 0 && (
                <div data-testid="preview-events">
                  <h3 className="font-display text-lg font-bold" style={{ color: content.theme_color }}>Upcoming Events</h3>
                  <div className="mt-2 space-y-2">
                    {content.events.map((ev, i) => (
                      <div key={i} className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                        <CalendarDays className="h-4 w-4 shrink-0 mt-0.5" style={{ color: content.theme_color }} />
                        <div className="min-w-0"><div className="text-sm font-semibold text-slate-800">{ev.title || "Untitled event"}</div><div className="text-xs text-slate-500">{[ev.date, ev.location].filter(Boolean).join(" · ")}</div>{ev.description && <div className="text-xs text-slate-600 mt-0.5">{ev.description}</div>}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-slate-100 pt-4 text-sm text-slate-500 flex flex-wrap gap-x-6 gap-y-1">
                {content.phone && <span>{content.phone}</span>}
                {content.email && <span>{content.email}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
